import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  autopilotCreditsShortfall,
  blogFetch,
  blogFetchAutogen,
  blogFetchNotices,
  blogFetchTenantStatus,
  blogPollTenantJobs,
  selectBlogArticles,
  selectBlogAutogen,
  selectBlogAutogenError,
  selectBlogAutopilotPrice,
  selectBlogCreditsBalance,
  selectBlogAutogenLoading,
  selectBlogLoaded,
  selectBlogLoading,
  selectBlogNotices,
  selectBlogNoticesError,
  selectBlogTenantStatus,
  selectBlogTenantStatusError,
  selectBlogTenantStatusLoading,
} from '@/store/slices/blog';

import { dateLocale } from '../../../../utils/locale';
import { resolveSummary, type WebsiteCardView } from '../../card-summary';
import { useWebsiteHubChrome } from '../../hub-chrome';
import { useBrandSettled } from '../../useBrandSettled';
import type { ReadStatus } from '../creatives/creatives-summary';
import { blogCopy } from './blog-copy';
import { articlesBeingWritten, deriveBlogSummary, type BlogCardInput } from './blog-summary';

/**
 * The Website Blog card: reads through the blog slice's own thunks, under the Blog page's own guards, so the Blog page
 * opens already loaded and the card says what the page and Home's Spotlight say (the credits notice is the
 * Spotlight's signal). Reads only: the articles and categories, the tenant switch, the Autopilot settings, the
 * notices, and one read of the articles being written (once per brand, no polling: the Blog page and the layout poll
 * while something is written). Nothing is asked until the displayed brand has caught up with the active one, the
 * brand the slice asks under, as on Home.
 *
 * The thunks are not aborted when the hub unmounts: they take no signal (the request runs to its end either way), and
 * an aborted read would be filed in the shared slice as that read's error, which the Blog page's guards then treat as
 * a failed load. A superseded brand's answer is dropped by the slice's own request stamps.
 */
export const useBlogCard = (): WebsiteCardView => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { openBuyCredits } = useWebsiteHubChrome();

  const brandId = useAppSelector((state) => state.casinoInstance.displayBrand?.id);
  const activeBrandId = useAppSelector((state) => state.casinoInstance.activeBrand?.id);
  // The displayed brand is the one the slice asks under: not mid-switch.
  const settled = brandId !== undefined && brandId === activeBrandId;
  // No brand will come (the list answered with none, or failed): nothing to read, so the card ends as failed.
  const brandSettled = useBrandSettled();
  const noBrand = brandId === undefined && brandSettled;

  const loaded = useAppSelector(selectBlogLoaded);
  const loading = useAppSelector(selectBlogLoading);
  const error = useAppSelector((state) => state.blog.error);
  const articles = useAppSelector(selectBlogArticles);
  const tenantStatus = useAppSelector(selectBlogTenantStatus);
  const tenantStatusLoading = useAppSelector(selectBlogTenantStatusLoading);
  const tenantStatusError = useAppSelector(selectBlogTenantStatusError);
  const autogen = useAppSelector(selectBlogAutogen);
  const autogenLoading = useAppSelector(selectBlogAutogenLoading);
  const autogenError = useAppSelector(selectBlogAutogenError);
  const notices = useAppSelector(selectBlogNotices);
  const noticesError = useAppSelector(selectBlogNoticesError);
  const jobs = useAppSelector((state) => state.blog.jobs);
  const balance = useAppSelector(selectBlogCreditsBalance);
  const price = useAppSelector(selectBlogAutopilotPrice);

  // Each read on the Blog page's own guard: once per brand (the slice resets on a switch), never again after a failure.
  React.useEffect(() => {
    if (settled && !loaded && !loading && error == null) void dispatch(blogFetch());
  }, [dispatch, settled, loaded, loading, error]);

  React.useEffect(() => {
    if (settled && tenantStatus === null && !tenantStatusLoading && tenantStatusError == null) {
      void dispatch(blogFetchTenantStatus());
    }
  }, [dispatch, settled, tenantStatus, tenantStatusLoading, tenantStatusError]);

  React.useEffect(() => {
    if (settled && autogen === null && !autogenLoading && autogenError == null) void dispatch(blogFetchAutogen());
  }, [dispatch, settled, autogen, autogenLoading, autogenError]);

  React.useEffect(() => {
    if (settled) void dispatch(blogFetchNotices());
    // `brandId` is the brand-switch trigger: a new brand is a new read.
  }, [dispatch, settled, brandId]);

  // One read of what is being written, per brand, once the list is in. A brand known to have no tenant is skipped:
  // every route under it answers 404.
  const jobsReadFor = React.useRef<string | undefined>(undefined);
  const provisioned = tenantStatus?.provisioned;
  React.useEffect(() => {
    if (!settled || !loaded || provisioned === false || jobsReadFor.current === brandId) return;
    jobsReadFor.current = brandId;
    void dispatch(blogPollTenantJobs());
  }, [dispatch, settled, loaded, provisioned, brandId]);

  const failed = (error != null && !loaded) || tenantStatusError != null;
  // The Autopilot settings and the notices decide the chip, the line and the primary action, so the card waits for
  // them too rather than swapping all three when they land. A failed one has answered: the card goes on without it.
  const autopilotAnswered = autogen !== null || autogenError != null;
  const noticesAnswered = notices !== null || noticesError != null;
  const allIn = loaded && tenantStatus !== null && autopilotAnswered && noticesAnswered;
  const status: ReadStatus = noBrand || (settled && failed) ? 'failed' : settled && allIn ? 'ready' : 'loading';
  // The open notice, while the balance is still short of the next article: the layout closes a notice the balance
  // covers (`useAutopilotCreditsSettle`), and until it has, the card must not ask for credits the owner already has.
  const creditsNeeded = autopilotCreditsShortfall({ needed: notices?.credits?.needed ?? null, balance, price });
  const input: BlogCardInput = {
    status,
    tenant: {
      provisioned: tenantStatus?.provisioned ?? false,
      enabled: tenantStatus?.enabled ?? false,
      banned: tenantStatus?.banned ?? false,
    },
    articles: articles.map((article) => ({
      id: article.id,
      status: article.status,
      updatedAt: article.updatedAt,
      publishedAt: article.publishedAt ?? null,
    })),
    autopilot: autogen && autogen.supported ? { enabled: autogen.enabled, nextRunAt: autogen.nextRunAt } : null,
    creditsNeeded,
    writing: articlesBeingWritten(jobs),
  };

  return resolveSummary(
    deriveBlogSummary(input),
    (c) => blogCopy(t, c, dateLocale(i18n.language)),
    // The one handler the summary can name: the hub's own sheet, primed with the notice's price, as Home primes it.
    (name) => (name === 'buy-credits' ? () => openBuyCredits(creditsNeeded ?? undefined) : undefined),
  );
};
